'use client';
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Dictionary, Notification } from '@witmetrics/utils';
import { UnauthorizedError } from '@witmetrics/utils/errors';
import { useActiveAccount, useDispatch } from '@witmetrics/data-store';
import { ACCOUNT_TYPES } from '@witmetrics/utils/constants';
import { isNewOrderNotification } from '@witmetrics/utils/notifications';
import { showErrorToaster } from '@/utils/toasters';
import { DIALOGS } from '@/constants/dialogs';
import { usePath } from '@/hooks/usePath';
import { useSockets } from '@/providers/SocketsProvider';
import { getCRMURL, getUnisonURL } from '@/api/getURL';
import { DEFAULT_DIALOGS } from './constants';
import {
  getNotificationAccount,
  isFileNotification,
  isLeadNotification,
  isProjectNotification,
  updateActiveAccount,
  updateReadNotification,
  useStoredAccountId,
} from './utils';

const { PRACTICE } = ACCOUNT_TYPES;

type AppStateValue = {
  activeDialogs: Dictionary<boolean>;
  setActiveDialogs: Dispatch<SetStateAction<{ [p: string]: boolean }>>;
  onLogin: () => void;
  onApiError: (err: any, message: string, cb?: Function) => void;
  onOpenNotification: (notification: Notification) => void;
  onOpenDialog: (dialogName: string) => void;
  onCloseDialog: (dialogName: string) => void;
};

const AppStateContext = createContext<AppStateValue>({
  activeDialogs: DEFAULT_DIALOGS,
  setActiveDialogs: () => null,
  onLogin: () => null,
  onApiError: () => null,
  onOpenNotification: () => null,
  onOpenDialog: () => null,
  onCloseDialog: () => null,
});

export default function AppStateProvider({ children }: PropsWithChildren) {
  const loginCallbacks = useRef<Function[]>([]);
  const dispatch = useDispatch();
  const activeAccount = useActiveAccount();
  const { setApiErrorCallback } = useSockets();
  const { navigate, pushParams, removeParams } = usePath();
  const [activeDialogs, setActiveDialogs] = useState(DEFAULT_DIALOGS);
  const [storedAccountId, setStoredAccountId] = useStoredAccountId();

  useEffect(() => {
    setApiErrorCallback(handleSocketError);
  }, []);

  useEffect(() => {
    if (activeAccount === undefined) return;
    if (activeAccount === null || activeAccount?.accountType !== PRACTICE) {
      handleOpenDialog(DIALOGS.ACCOUNTS);
    } else if (activeAccount.accountId !== storedAccountId) {
      handleResetAccountItems();
    }
  }, [activeAccount]);

  const handleResetAccountItems = () => {
    setStoredAccountId(activeAccount!.accountId);
  };

  const handleOpenDialog = (dialogName: string) => {
    setActiveDialogs((dialogs) => ({ ...dialogs, [dialogName]: true }));
  };

  const handleCloseDialog = (dialogName: string) => {
    setActiveDialogs((dialogs) => ({ ...dialogs, [dialogName]: false }));
  };

  const handleSocketError = () => {
    handleApiError(new UnauthorizedError('', ''), '');
  };

  const handleApiError = (err: any, toasterMessage: string, cb?: Function) => {
    if (err instanceof UnauthorizedError) {
      if (cb) loginCallbacks.current.push(cb);
      if (!activeDialogs[DIALOGS.LOGIN]) handleOpenDialog('login');
    } else {
      showErrorToaster(toasterMessage);
      throw err;
    }
  };

  const handleLogin = () => {
    if (loginCallbacks.current) {
      loginCallbacks.current.forEach((cb) => cb());
    }
    handleCloseDialog(DIALOGS.LOGIN);
    loginCallbacks.current = [];
  };

  const handleOpenNotification = async (notification: Notification) => {
    // Make sure the correct account is active
    const { accountType, accountId } = getNotificationAccount(notification);
    if (
      activeAccount?.accountType !== accountType ||
      activeAccount?.accountId !== accountId
    ) {
      const account = await updateActiveAccount(accountType, accountId);
      if (accountType === PRACTICE) dispatch.addActiveAccount(account);
    }

    // No need to wait on this, the socket event will handle it
    if (!notification.isRead) updateReadNotification(notification.id);

    // Determine what to open / navigate to
    if (isProjectNotification(notification)) {
      let params: Dictionary<number> = {
        activeProjectId: notification.content.unisonProjectId,
      };
      if ('noteId' in notification.content) {
        params.highlightedNoteId = notification.content.noteId;
      }
      if ('taskId' in notification.content) {
        params.highlightedTaskId = notification.content.taskId;
      }
      return navigate(getUnisonURL(), params);
    } else if (isFileNotification(notification)) {
      if ('unisonProjectId' in notification.content) {
        return navigate(getUnisonURL(), {
          activeProjectId: notification.content.unisonProjectId,
          activeFileId: notification.content.fileId,
          highlightedNoteId: notification.content.noteId,
        });
      } else {
        showErrorToaster('File notifications are not yet supported');
      }
    } else if (isNewOrderNotification(notification)) {
      return navigate(getUnisonURL(), {
        activeOrderId: notification.content.orderId,
      });
    } else if (isLeadNotification(notification)) {
      return navigate(getCRMURL(), {
        activeLeadId: notification.content.leadId,
      });
    } else {
      showErrorToaster('Error opening notification');
      throw Error(
        // @ts-ignore
        `Unrecognized notificationTypeId: ${notification.notificationTypeId}`
      );
    }
  };

  const contextValue = {
    activeDialogs,
    setActiveDialogs,
    onLogin: handleLogin,
    onApiError: handleApiError,
    onOpenNotification: handleOpenNotification,
    onOpenDialog: handleOpenDialog,
    onCloseDialog: handleCloseDialog,
  };

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw Error('useAppState must be used within the AppStateProvider');
  }
  return context;
}
